import type { ApiEndpoints } from '@avisoma/shared';
import { formatDateTime } from '../../lib/dateFormat';

export type StatsResponse = ApiEndpoints<string>['/api/projects/:id/webhook-deliveries/stats']['GET']['response'];

// One stacked series: its count key, legend label, and fill color. Shared by the
// three breakdowns (outcome / event type / session).
export type Series = { key: string; label: string; fill: string };

// One series per outcome, colored to match the WebhookDeliveries badge palette
// (outcomeColor): success=green-500, published=emerald-500, retry=amber-500,
// dropped=red-500. publish-failed also maps to "red" in the badge, but two
// identical reds are indistinguishable when stacked adjacent, so it uses the
// darker red-700 — the segments still read as red while staying separable.
export const OUTCOME_SERIES: Series[] = [
  { key: 'success', label: 'Success', fill: '#22c55e' },
  { key: 'published', label: 'Published', fill: '#10b981' },
  { key: 'retry', label: 'Retry', fill: '#f59e0b' },
  { key: 'dropped', label: 'Dropped', fill: '#ef4444' },
  { key: 'publish-failed', label: 'Publish failed', fill: '#b91c1c' },
];

// Categorical palette for event-type series (no shared event-type→color map
// exists in WebhookEventTypeChips to reuse). Distinct Tailwind-500 hues that
// stay legible in both light and dark mode; assigned by alphabetical key order
// so colors stay stable for a given key set across refetches/zooms (a newly
// appearing type can shift later colors — acceptable), cycling past 12 types.
const EVENT_TYPE_PALETTE = [
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#10b981', // emerald-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
  '#f97316', // orange-500
  '#6366f1', // indigo-500
  '#84cc16', // lime-500
  '#06b6d4', // cyan-500
  '#a855f7', // purple-500
];

// Dedicated palette for retry-session series: the event-type palette minus the
// greens (emerald/teal/lime — they'd collide with the pinned Initial-upload
// green) and minus the near-duplicate purple (violet already covers that hue).
// Eight distinct non-green hues, none close to the pinned neutrals, so a retry
// session never reads as Initial upload / System / Legacy / Other.
const RETRY_PALETTE = [
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#f97316', // orange-500
  '#6366f1', // indigo-500
  '#06b6d4', // cyan-500
];

// Event-type series derived from the union of bucket-count keys, ordered
// alphabetically so each key maps to a fixed color and stable stacking order
// regardless of how volume shifts between refetches/zooms.
export function eventTypeSeries(buckets: StatsResponse['buckets']): Series[] {
  const keys = new Set<string>();
  for (const b of buckets) {
    for (const key of Object.keys(b.counts as Record<string, number>)) keys.add(key);
  }
  return [...keys]
    .sort((a, b) => a.localeCompare(b))
    .map((key, i) => ({ key, label: key, fill: EVENT_TYPE_PALETTE[i % EVENT_TYPE_PALETTE.length] }));
}

// Pinned colors for the four fixed session groups: initial-upload is green (it's
// the "good default" state), the rest are distinct neutrals so they never
// collide with a retry session's palette hue. Retry sessions (uuid keys) get a
// RETRY_PALETTE color by a stable HASH of the id — deliberately NOT rank/index-
// based, so a session keeps its color even as top-N membership shifts between
// zooms.
const SESSION_PINNED_COLORS: Record<string, string> = {
  'initial-upload': '#22c55e', // green-500
  system: '#64748b', // slate-500
  legacy: '#a1a1aa', // zinc-400
  other: '#78716c', // stone-500
};

// Deterministic string hash → palette index. Stable across refetches so a retry
// session's color is a function of its uuid alone (see SESSION_PINNED_COLORS).
function hashStringToIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % mod;
}

// Session series from the server's ordered series metadata. Pinned keys use their
// fixed color + server label; retry sessions hash their uuid into RETRY_PALETTE
// and append the session's first-seen datetime to the label.
export function sessionSeries(meta: StatsResponse['series']): Series[] {
  return meta.map((m) => {
    const pinned = SESSION_PINNED_COLORS[m.key];
    const fill = pinned ?? RETRY_PALETTE[hashStringToIndex(m.key, RETRY_PALETTE.length)];
    const label = pinned || !m.firstSeen ? m.label : `${m.label} · ${formatDateTime(m.firstSeen)}`;
    return { key: m.key, label, fill };
  });
}

export type ChartRow = { start: string; total: number } & Record<string, number | string>;

export function toChartRows(buckets: StatsResponse['buckets'], series: Series[]): ChartRow[] {
  return buckets.map((b) => {
    const counts = b.counts as Record<string, number>;
    const total = series.reduce((sum, s) => sum + (counts[s.key] ?? 0), 0);
    return { start: b.start, total, ...counts };
  });
}
